import { defineStore } from "pinia";
import { useMate } from "@/stores/mate";
import { helperImg } from "./globalModalHelper";
import { sessionStorageGet } from "@/tools/sessionStorage";
import i18n from "@/locales";
import { showConfirmDialog, showLoadingToast } from "vant";
import { StorageGet, StorageSet } from "@/tools/localStorage";
import {
  Entity,
  Mate,
  Media,
  ModuleEntityType,
  Moment,
  Reply,
} from "@/types/entity";
import api from "@/modules/api";
import { useGlobalUser } from "./globalUser";
import { ReceivedType } from "@/types/payment";
import { AIWriteType } from "@/enums/mateEnums";
import { ReplyEntityType } from "@/enums";
import { ActionSheetAction } from "vant/es";
import { getLanguageModelRatio } from "@/enums/chatEnums";

type FunctionType = (content?: string) => unknown | Promise<unknown>;

interface RichTextOption {
  // module
  entityId?: number;
  entityType?: ModuleEntityType;
  // roleplay
  mateId?: number;
  questionId?: number;
  // mateMail
  senderId?: number;
  recipientId?: number;
  subject?: string;
  body?: string;

  // reply
  replyEntityId?: number;
  replyEntityType?: ReplyEntityType;
  parentId?: number;
  content?: string;
  // mateId 发送人
  // recipientId 接收人
}

export interface CustomGlobalActionSheetItem {
  content: string;
  className?: string;
  callback?: () => void;
  paymentInfo?: {
    type: "crystal" | "power";
    amount: number;
  };
}
export const useGlobalModal = defineStore("globalModal", {
  state: () => {
    return {
      // 展示获得东西的弹框
      showReceivedModal: false,
      receiveList: [] as {
        amount?: number;
        type?: ReceivedType;
      }[],
      receiveType: "Subscription" as string,

      // 展示购买单个媒体资源的弹框
      showBuyItemModal: false,
      mateMediaItem: {} as MateMedia,

      // 展示 helper 弹框
      showHelperModal: false,
      helperType: "",
      helperList: [] as Helper[],

      // 展示订阅弹框
      showSubscribeModal: false,

      // 展示订阅过期弹框
      showSubscribeExpiredModal: false,

      // 展示服务器维护弹框
      showServerUpgradingModal: false,

      // 展示 小程序 弹框
      showMiniProgramModal: false,
      miniProgramUrl: "",

      // 展示 媒体预览 弹框
      showMediaPreviewModal: false,
      mediaPreviewParams: {} as { withDownloadTips?: boolean },
      mediaList: [] as string[],
      startPosition: 0,

      // 展示全局富文本编辑弹框
      showRichTextEditor: false,
      richTextTitle: "",
      richTextAiType: undefined as AIWriteType | undefined,
      richText: "",
      richTextType: "richText" as "richText" | "longText",
      richTextModuleName: "",
      richTextMaxLength: undefined as number | undefined,
      richTextCallback: undefined as FunctionType | undefined,
      richTextCallback2: undefined as FunctionType | undefined,
      richTextOption: undefined as RichTextOption | undefined,

      // 展示单文本输入弹框
      showTextInputModal: false,
      textInputTitle: "",
      textInputDescription: "",
      textInputInitValue: "",
      textInputCallback: undefined as FunctionType | undefined,

      // 展示 新站内信 弹框
      showNewSiteMessageModal: false,
      newSiteMessage: {} as {
        uuid: string;
        title: string;
        content: string;
      },

      showCrystalNotEnoughModal: false,
      spentCrystalAmount: 5, // 调用付款接口之前，需要初始化改值

      showPowerNotEnoughModal: false,
      spentPowerAmount: 5, // 调用付款接口之前，需要初始化改值
      paidPowerCallback: null as FunctionType | null,

      // 展示 进入APP 弹框
      showEnterAppModal: false,

      // 展示 回复编辑 弹框
      showReplyEditModal: false,
      replyEditReply: {} as Reply,
      replyEditType: "moment" as ReplyEntityType,
      replyEditMoment: {} as Moment | undefined,
      replyEditEntity: {} as Entity | undefined,
      replyEditMate: {} as Mate | undefined,

      // 全局 actionSheet
      showGlobalActionSheet: false,
      globalActionSheetList: [] as ActionSheetAction[],

      // 自定义全局 actionSheet
      showCustomGlobalActionSheet: false,
      customGlobalActionSheetTitle: "",
      customGlobalActionSheetList: [] as CustomGlobalActionSheetItem[],

      // 展示 选择模型 弹框
      showSelectModelModal: false,
      selectModelList: [] as {
        name: string;
        id: number;
      }[],

      // 展示 任务详情 弹框
      showTaskPageModal: false,
    };
  },
  actions: {
    openReceivedModal(
      receivedList: {
        amount?: number;
        type?: ReceivedType;
      }[],
      type:
        | "paymentReceivedList"
        | "equipmentDecompose"
        | "CrystalOrderPay"
        | "Subscription"
    ) {
      this.receiveList = receivedList;
      this.showReceivedModal = true;
      this.receiveType = type;
      useGlobalUser().refresh();
    },
    openBuyItemModal(mateMedia: MateMedia, media: Media, mate: Mate) {
      if ([7, 8].includes(mateMedia.visibility) && !mate.superAdmirePrice) {
        mate.superAdmirePrice = useMate().mateIdMap.get(
          mate.id!
        )!.superAdmirePrice;
      }
      mateMedia.media = media;
      mateMedia.mate = mate;
      this.mateMediaItem = mateMedia;
      this.showBuyItemModal = true;
    },
    openHelperModal(type: HelperModalType, forceOpen = false) {
      if (!forceOpen && sessionStorageGet(`helper.${type}`)) return;
      this.helperType = type;
      this.helperList = helperImg[type].map((img, i) => ({
        img,
        content: `helper.${type}.${i}`,
      }));
      this.showHelperModal = true;
    },

    // 展示服务器维护弹框
    openServerUpgradingModal(forceOpen = false) {
      const knowServerUpgrading = StorageGet("know:serverUpgrading");
      if (this.showServerUpgradingModal || (knowServerUpgrading && !forceOpen))
        return;
      this.showServerUpgradingModal = true;

      const _startTime = new Date("2024-11-14T15:00:00Z").getTime();
      const _endTime = new Date("2024-11-15T06:00:00Z").getTime();
      const now = new Date().getTime();

      const checkTimeStartTime = new Date("2024-11-14T08:00:00Z").getTime();
      const checkTimeEndTime = new Date("2024-11-15T12:00:00Z").getTime();
      if (now >= checkTimeStartTime && now <= checkTimeEndTime) {
        showConfirmDialog({
          title: i18n.global.t("Message.serverUpgradingTitle"),
          message: i18n.global.t("Message.serverUpgrading", {
            startTime: new Date(_startTime).toLocaleString(),
            endTime: new Date(_endTime).toLocaleString(),
          }),
          confirmButtonText: i18n.global.t("Buttons.confirm"),
          cancelButtonText: i18n.global.t("Buttons.cancel"),
        })
          .then(() => {
            StorageSet("know:serverUpgrading", true);
            this.showServerUpgradingModal = false;
          })
          .catch(() => {
            this.showServerUpgradingModal = false;
          });
      }
    },

    openMiniProgramModal(url: string) {
      this.miniProgramUrl = url;
      this.showMiniProgramModal = true;
    },

    openMediaPreviewModal(
      mediaList: string[],
      startPosition = 0,
      params: { withDownloadTips?: boolean } = {}
    ) {
      this.mediaPreviewParams = params;
      this.mediaList = mediaList;
      this.startPosition = startPosition;
      this.showMediaPreviewModal = true;
    },

    openRichEditor(params: {
      title: string;
      content: string;
      aiType?: AIWriteType;
      moduleName?: string;
      callback: FunctionType;
      callback2?: FunctionType;
      options?: RichTextOption;
      type?: "richText" | "longText";
      maxLength?: number;
    }) {
      this.richText = params.content;
      this.richTextTitle = params.title;
      this.richTextModuleName = params.moduleName || "";
      this.richTextAiType = params.aiType;
      this.showRichTextEditor = true;
      this.richTextOption = params.options;
      this.richTextType = params.type || "richText";
      this.richTextCallback = params.callback;
      this.richTextCallback2 = params.callback2;
      this.richTextMaxLength = params.maxLength;
    },

    openTextInputModal(params: {
      title: string;
      description?: string;
      initValue?: string;
      callback: FunctionType;
    }) {
      this.textInputTitle = params.title;
      this.textInputDescription = params.description || "";
      this.textInputInitValue = params.initValue || "";
      this.textInputCallback = params.callback;
      this.showTextInputModal = true;
    },

    async openNewSiteMessageModal(params: { uuid: string; title: string }) {
      const loading = showLoadingToast({
        message: i18n.global.t("Message.loading"),
        duration: 0,
      });
      try {
        const res = await api.readSiteMessage(params.uuid);
        this.newSiteMessage = {
          ...params,
          content: res.item.content,
        };
        this.showNewSiteMessageModal = true;
        loading.close();
      } catch {
        loading.close();
      }
    },

    shouldOpenPowerNotEnoughModal(params: {
      spentPowerAmount: number;
      cb: FunctionType;
      plusModelRatio?: boolean;
    }) {
      const {
        spentPowerAmount: _spentPowerAmount,
        cb,
        plusModelRatio,
      } = params;
      const userStore = useGlobalUser();
      if (plusModelRatio && userStore.isFreeModel()) return false;

      const { preferredModel } = userStore.user;

      let spentPowerAmount = _spentPowerAmount;
      if (plusModelRatio) {
        spentPowerAmount *= getLanguageModelRatio(preferredModel);
      }

      this.spentPowerAmount = spentPowerAmount;
      const missPowerAmount = spentPowerAmount - userStore.user.powerAmount;
      this.paidPowerCallback = cb;
      if (missPowerAmount > 0) {
        this.showPowerNotEnoughModal = true;
        userStore.refresh();
      }
      return missPowerAmount > 0;
    },

    shouldOpenCrystalNotEnoughModal(spentCrystalAmount: number) {
      const userStore = useGlobalUser();
      const missCrystalAmount =
        spentCrystalAmount - userStore.user.crystalQuantity;
      this.spentCrystalAmount = spentCrystalAmount;
      if (missCrystalAmount > 0) {
        this.showCrystalNotEnoughModal = true;
        userStore.refresh();
      }
      return missCrystalAmount > 0;
    },

    openReplyEditModal(params: {
      moment?: Moment;
      reply: Reply;
      type: ReplyEntityType;
      entity?: Entity;
      mate?: Mate;
    }) {
      this.replyEditReply = params.reply;
      this.replyEditType = params.type;

      this.replyEditMoment = params.moment;
      this.replyEditEntity = params.entity;
      this.replyEditMate = params.mate;
      this.showReplyEditModal = true;
    },

    openGlobalActionSheet(list: ActionSheetAction[]) {
      this.globalActionSheetList = list;
      this.showGlobalActionSheet = true;
    },

    openCustomGlobalActionSheet(params: {
      title: string;
      list: CustomGlobalActionSheetItem[];
    }) {
      this.customGlobalActionSheetTitle = params.title;
      this.customGlobalActionSheetList = params.list;
      this.showCustomGlobalActionSheet = true;
    },
  },
});
